import { defaultLocale, type Locale } from "@/lib/i18n/config";
import { ALL_KINDS, KIND_ROUTE_SLUG } from "@/lib/routing";
import { localesForItem } from "@/lib/seo/alternate-locales";

/**
 * Whether a per-record page actually exists under a locale.
 *
 * The sitemap and the hreflang cluster already answer this question -- a locale only gets a page
 * when the catalog has a real translation for that record, so the page never renders English under
 * a non-English URL (see alternate-locales.ts#localesForItem,
 * guides/registry.ts#localesForGuide, news/registry.ts#localesForNews and
 * scripts/generate-sitemap.ts#availableLocalesFor).
 *
 * Nothing taught the *links* the same rule. A prerendered index like /id/recipes shipped 149 cards
 * pointing at detail pages the generator had deliberately not written -- ~1,342 internal links to
 * real 404s across /id and /th, the single largest source of crawl waste in the 404 report. This
 * module is where the link side of that rule lives.
 */

const ITEM_ROUTE_SLUGS = new Set(ALL_KINDS.map((kind) => KIND_ROUTE_SLUG[kind]));

type LocaleResolver = (id: string) => Locale[];

const resolvers = new Map<string, LocaleResolver>();

/**
 * Teach `linkLocaleFor` about a route whose availability rule lives outside this module.
 *
 * Guides and news are keyed by their own registries, which are eager `import.meta.glob`s. Importing
 * them here would pull every guide and news body into the eager Layout chunk, since LocaleLink
 * lives there -- so they register themselves instead, from inside their already-lazy chunks. Any
 * component that renders a guide or news link has necessarily imported the corresponding registry,
 * so the resolver is always in place before the link it governs is rendered; an unregistered route
 * simply keeps the requested locale, which is the pre-existing behaviour.
 */
export function registerLocaleResolver(routeSlug: string, resolve: LocaleResolver): void {
  resolvers.set(routeSlug, resolve);
}

/**
 * The locale a link should actually use for `path`.
 *
 * Returns the requested locale when the page exists in it, and the default locale otherwise, so the
 * reader lands on the English page that does exist instead of a 404. That is also what the target
 * page's own hreflang advertises, which keeps the link and the head tag telling Google one story.
 */
export function linkLocaleFor(path: string, locale: Locale): Locale {
  if (locale === defaultLocale) return locale;

  const segments = path.split("/").filter(Boolean);
  // Only `/<route>/<id>` is gated. Index routes exist in every locale (their copy lives in the
  // dictionaries), and deeper paths are not per-record pages.
  if (segments.length !== 2) return locale;

  const [head, id] = segments;

  const registered = resolvers.get(head);
  if (registered) return registered(id).includes(locale) ? locale : defaultLocale;

  if (!ITEM_ROUTE_SLUGS.has(head)) return locale;

  return localesForItem(id).includes(locale) ? locale : defaultLocale;
}
